// Drives the browser screen recorder from the admin-configured policy.
// Mounted inside the agent shell (Layout). Shows a one-time consent pill when
// recording is enabled, then auto starts/stops recording for the enabled modes:
//   call  → while a live call is bridged
//   ready → while the agent is Available (Ready)
//   omni  → while handling an omnichannel conversation
import { useEffect, useRef, useState } from "react";
import { ScreenShare, Circle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScreenRecorder } from "@/hooks/useScreenRecorder";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ScreenRecorderController({ presence }) {
  const [policy, setPolicy] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const {
    streamReady, recording, error,
    requestConsent, startSession, stopSession,
  } = useScreenRecorder();

  const callActiveRef = useRef(false);
  const omniActiveRef = useRef(false);
  const callIdRef = useRef(null);
  const [trigger, setTrigger] = useState(0); // bump to re-evaluate shouldRecord

  // Load the effective policy for this agent's active campaign.
  const loadPolicy = () => {
    api.get("/me/screen-recording/policy")
      .then((r) => setPolicy(r.data || null))
      .catch(() => setPolicy(null));
  };
  useEffect(() => { loadPolicy(); }, []);
  useEffect(() => {
    // Layout dispatches "c7:campaign-changed" (singular) when the agent joins
    // or switches a campaign — re-resolve the policy for the new campaign.
    const onCamp = () => loadPolicy();
    window.addEventListener("c7:campaign-changed", onCamp);
    return () => window.removeEventListener("c7:campaign-changed", onCamp);
  }, []);

  // Track live triggers via existing window events.
  useEffect(() => {
    const onActivity = (e) => {
      callActiveRef.current = !!e.detail?.inCall;
      // Manual calls surface their call_id here (predictive/auto use dialer:bridged).
      if (e.detail?.call_id) callIdRef.current = e.detail.call_id;
      setTrigger((t) => t + 1);
    };
    const onBridged = (e) => {
      callActiveRef.current = true;
      if (e.detail?.call_id) callIdRef.current = e.detail.call_id;
      if (e.detail?.channel) callIdRef.current = callIdRef.current || e.detail.channel;
      setTrigger((t) => t + 1);
    };
    const onEnded = () => {
      callActiveRef.current = false;
      setTrigger((t) => t + 1);
    };
    const onOmni = (e) => {
      omniActiveRef.current = !!e.detail?.active;
      setTrigger((t) => t + 1);
    };
    window.addEventListener("c7:call-activity", onActivity);
    window.addEventListener("dialer:bridged", onBridged);
    window.addEventListener("dialer:ended", onEnded);
    window.addEventListener("c7:conversation-active", onOmni);
    return () => {
      window.removeEventListener("c7:call-activity", onActivity);
      window.removeEventListener("dialer:bridged", onBridged);
      window.removeEventListener("dialer:ended", onEnded);
      window.removeEventListener("c7:conversation-active", onOmni);
    };
  }, []);

  // State machine: compute whether we SHOULD be recording, then converge.
  useEffect(() => {
    if (!policy?.enabled || !streamReady) return;
    const modes = policy.modes || [];
    const ready = (presence || "Offline") === "Available";
    const want =
      (modes.includes("call") && callActiveRef.current) ||
      (modes.includes("ready") && ready) ||
      (modes.includes("omni") && omniActiveRef.current);

    if (want && !recording) {
      const mode = callActiveRef.current ? "call" : (ready ? "ready" : "omni");
      startSession({
        mode,
        callId: callIdRef.current,
        campaignId: policy.campaign_id,
        quality: policy.quality,
      });
    } else if (!want && recording) {
      stopSession(callIdRef.current);
      callIdRef.current = null;
    }
  }, [policy, streamReady, recording, presence, trigger, startSession, stopSession]);

  if (!policy?.enabled) return null;

  const grant = async () => {
    const res = await requestConsent({
      withAudio: policy.audio !== false,
      requireFullScreen: policy.require_full_screen !== false,
    });
    if (res.ok) { toast.success("Screen recording enabled for this session"); return; }
    if (res.reason === "wrong_surface") {
      toast.error("Please share your ENTIRE screen — a single window or tab isn't allowed.");
      flag("wrong_surface", res.surface);
    } else {
      toast.error("Screen share declined — recording is off");
      flag("declined", null);
    }
  };

  const flag = (reason, surface) => {
    api.post("/me/screen-recording/flag", {
      reason, surface: surface || null, campaign_id: policy?.campaign_id || null,
    }).catch(() => {});
  };

  return (
    <div className="fixed bottom-20 right-3 z-50" data-testid="screen-recorder-controller">
      {!streamReady && !dismissed && (
        <div
          data-testid="screen-recorder-consent"
          className="flex items-center gap-3 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 shadow-lg dark:border-amber-700 dark:bg-amber-950"
        >
          <ScreenShare className="w-4 h-4 text-amber-600 shrink-0" />
          <div className="text-xs text-amber-900 dark:text-amber-100 max-w-[220px]">
            Screen recording is required for your campaign.{policy.require_full_screen !== false ? " Share your ENTIRE screen (not a single window)." : " Click to share your screen."}
          </div>
          <Button
            size="sm"
            data-testid="screen-recorder-consent-btn"
            className="h-7 rounded-md bg-amber-600 hover:bg-amber-700 text-white"
            onClick={grant}
          >
            Start
          </Button>
          <button
            data-testid="screen-recorder-dismiss"
            className="text-amber-500 hover:text-amber-700"
            title="Hide (recording stays off until you share)"
            onClick={() => setDismissed(true)}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {streamReady && (
        <div
          data-testid="screen-recorder-status"
          className="flex items-center gap-2 rounded-full border bg-background/90 px-3 py-1.5 shadow-md backdrop-blur"
        >
          <Circle
            className={`w-3 h-3 ${recording ? "fill-red-500 text-red-500 animate-pulse" : "fill-slate-300 text-slate-300"}`}
          />
          <span className="text-xs font-medium">
            {recording ? "Recording screen" : "Screen share ready"}
          </span>
        </div>
      )}
    </div>
  );
}
